import { z } from 'zod';
import { validateNumberFormat } from '../billing/numbering.js';
import { DEFAULT_DEVICE_SETTINGS, KITCHEN_TIMING } from '../constants/devices.js';
import { ORDER_SOURCE_KEYS, ORDER_TYPE_KEYS, PAYMENT_MODE_KEYS, ROUNDING_MODES } from '../constants/pos.js';
import { isoDate, objectId, optional, text } from './common.js';

// ---------------------------------------------------------------- settings (Settings → Offline & displays)
const d = DEFAULT_DEVICE_SETTINGS;
const minutes = (max) => z.coerce.number().int().min(0).max(max);

export const offlineSettingsSchema = z.strictObject({
  enabled: z.boolean(),
  invoiceFormat: z
    .string()
    .trim()
    .superRefine((v, ctx) => {
      if (!v.includes('{DEVICE}')) ctx.addIssue({ code: 'custom', message: 'Must include {DEVICE}' });
      const r = validateNumberFormat(v);
      if (!r.ok) ctx.addIssue({ code: 'custom', message: r.error });
    }),
  allowNewCustomers: z.boolean(),
  allowCredit: z.boolean(),
  // Added after Phase 6: defaults keep settings saved before then valid.
  allowHolds: z.boolean().default(d.offline.allowHolds),
  allowUserSwitching: z.boolean().default(d.offline.allowUserSwitching),
  userSwitchDays: z.coerce.number().int().min(1).max(90).default(d.offline.userSwitchDays),
  localKitchen: z.boolean().default(d.offline.localKitchen),
});
export const kdsSettingsSchema = z.strictObject({
  warnMinutes: minutes(120).refine((v) => v >= 1, 'At least 1 minute'),
  lateMinutes: minutes(240).refine((v) => v >= 1, 'At least 1 minute'),
  sound: z.boolean(),
  hideServedAfterMinutes: minutes(60),
});
export const displaySettingsSchema = z.strictObject({
  enabled: z.boolean(),
  showPreparing: z.boolean(),
  readyMinutes: minutes(120).refine((v) => v >= 1, 'At least 1 minute'),
  chime: z.boolean(),
});
export const selfOrderSettingsSchema = z
  .strictObject({
    payAtCounter: z.boolean(),
    payByUpi: z.boolean(),
    kitchen: z.enum(KITCHEN_TIMING.map((k) => k.key)),
    orderTypes: z.array(z.enum(ORDER_TYPE_KEYS)).min(1, 'Offer at least one order type'),
    idleSeconds: z.coerce.number().int().min(20).max(600),
    autoCancelMinutes: minutes(720),
    welcomeText: z.string().trim().min(1).max(80),
  })
  .refine((s) => s.payAtCounter || s.payByUpi, { message: 'Allow at least one way to pay', path: ['payAtCounter'] });

export const deviceSettingsFields = {
  offline: offlineSettingsSchema.default({ ...d.offline }),
  kds: kdsSettingsSchema.default({ ...d.kds }),
  display: displaySettingsSchema.default({ ...d.display }),
  selfOrder: selfOrderSettingsSchema.default({ ...d.selfOrder, orderTypes: [...d.selfOrder.orderTypes] }),
};

// ---------------------------------------------------------------- offline sync (POST /sync/bills)
const paise = z.coerce.number().int().min(0).max(100_000_000_00);
const signedPaise = z.coerce.number().int().min(-100_000_000_00).max(100_000_000_00);

/** What the device priced and printed for each line, so the synced bill matches the receipt. */
const offlineLine = z.strictObject({
  lineId: z.string().uuid(),
  itemId: objectId,
  variantId: optional(objectId),
  modifierOptionIds: z.array(objectId).max(40).default([]),
  qty: z.coerce.number().int().min(1).max(999),
  note: optional(text(140)),
  discount: z
    .discriminatedUnion('type', [
      z.strictObject({ type: z.literal('percent'), valueBp: z.coerce.number().int().min(0).max(10_000) }),
      z.strictObject({ type: z.literal('flat'), valuePaise: paise }),
    ])
    .nullable()
    .optional(),
  unitPricePaise: paise,
  priceMode: z.enum(['inclusive', 'exclusive']),
  gstRateBp: z.coerce.number().int().min(0).max(10_000),
  cessRateBp: z.coerce.number().int().min(0).max(10_000).default(0),
  name: text(120),
  variantName: optional(text(80)),
});

const offlineDiscount = z
  .discriminatedUnion('type', [
    z.strictObject({
      type: z.literal('percent'),
      valueBp: z.coerce.number().int().min(0).max(10_000),
      reason: optional(text(120)),
    }),
    z.strictObject({ type: z.literal('flat'), valuePaise: paise, reason: optional(text(120)) }),
  ])
  .nullable()
  .optional();
const offlineNewCustomer = z
  .strictObject({
    phone: z
      .string()
      .trim()
      .regex(/^\+?\d{10,15}$/),
    name: optional(text(120)),
  })
  .nullable()
  .optional();
/** Charges and rounding exactly as the device applied them (settings may change meanwhile). */
const offlineCharges = z
  .array(
    z.strictObject({
      key: text(20),
      label: text(80),
      kind: z.enum(['percent', 'flat', 'per_item']),
      valueBp: z.coerce.number().int().min(0).max(10_000).optional(),
      valuePaise: paise.optional(),
      gstRateBp: z.coerce.number().int().min(0).max(10_000),
      cessRateBp: z.coerce.number().int().min(0).max(10_000).default(0),
    }),
  )
  .max(5)
  .default([]);
/** The server copy's updatedAt when the device loaded a held order (null: started on this device). */
const baseUpdatedAt = z.coerce.date().nullable().optional();

export const offlineBillSchema = z.strictObject({
  clientUuid: z.string().uuid(),
  capturedAt: z.coerce.date(),
  capturedBy: objectId,
  shiftId: optional(objectId),
  invoiceNo: z.string().trim().min(1).max(16),
  invoiceSeq: z.coerce.number().int().min(1).max(999_999_999),
  fy: z.string().regex(/^\d{4}$/),
  businessDate: isoDate,
  tokenNo: z.coerce.number().int().min(1).max(9_999_999),
  orderType: z.enum(ORDER_TYPE_KEYS),
  source: z.enum(ORDER_SOURCE_KEYS).default('counter'),
  tableNo: optional(text(12)),
  customerId: optional(objectId),
  newCustomer: offlineNewCustomer,
  lines: z.array(offlineLine).min(1).max(200),
  billDiscount: offlineDiscount,
  baseUpdatedAt,
  payments: z
    .array(
      z.strictObject({
        mode: z.enum(PAYMENT_MODE_KEYS),
        amountPaise: paise,
        tenderedPaise: paise.optional(),
        reference: optional(text(60)),
      }),
    )
    .max(10),
  notes: optional(text(300)),
  charges: offlineCharges,
  rounding: z.enum(ROUNDING_MODES),
  totals: z.strictObject({ grandTotalPaise: paise, roundOffPaise: signedPaise }).passthrough(),
});

/**
 * An order held or sent to the kitchen while offline (POST /sync/holds). Each save on the device
 * gets a new syncId, so a retried upload is recognised. kotQty: how much of each line the
 * kitchen already got (printed or local screen tickets), so the server never sends it again.
 */
export const offlineHoldSchema = z.strictObject({
  clientUuid: z.string().uuid(),
  syncId: z.string().uuid(),
  capturedAt: z.coerce.date(),
  capturedBy: objectId,
  baseUpdatedAt,
  tokenNo: z.coerce.number().int().min(1).max(9_999_999).nullable().optional(),
  orderType: z.enum(ORDER_TYPE_KEYS),
  source: z.enum(ORDER_SOURCE_KEYS).default('counter'),
  tableNo: optional(text(12)),
  guestCount: z.coerce.number().int().min(1).max(500).nullable().optional(),
  customerId: optional(objectId),
  newCustomer: offlineNewCustomer,
  lines: z
    .array(offlineLine.extend({ kotQty: z.coerce.number().int().min(0).max(999).default(0) }))
    .min(1)
    .max(200),
  billDiscount: offlineDiscount,
  notes: optional(text(300)),
  charges: offlineCharges,
  rounding: z.enum(ROUNDING_MODES),
});

// ---------------------------------------------------------------- KDS & displays
export const KOT_STATUSES = Object.freeze(['new', 'preparing', 'ready', 'served', 'cancelled']);
export const kotStatusSchema = z.strictObject({ status: z.enum(['new', 'preparing', 'ready', 'served']) });
export const kdsQuery = z.strictObject({ station: optional(text(40)) });
export const displayParam = z.strictObject({ outletId: objectId });

// ---------------------------------------------------------------- self-order kiosk
export const selfOrderSchema = z.strictObject({
  clientUuid: z.string().uuid(),
  orderType: z.enum(ORDER_TYPE_KEYS),
  payWith: z.enum(['counter', 'upi']),
  customerName: optional(text(40)),
  lines: z
    .array(
      z.strictObject({
        lineId: z.string().uuid(),
        itemId: objectId,
        variantId: optional(objectId),
        modifierOptionIds: z.array(objectId).max(40).default([]),
        qty: z.coerce.number().int().min(1).max(50),
        note: optional(text(140)),
      }),
    )
    .min(1, 'Add at least one item')
    .max(60),
  expectedTotalPaise: paise.optional(),
});
